import { useEffect, useState } from 'react'
import { Paperclip, FileText, FileCode2, Plus, Trash2 } from 'lucide-react'
import api from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/components/ui/toast'
import { formatBRL, formatDate } from '@/lib/utils'

function assuntoPadrao(nf) {
  return `NFS-e ${nf?.numero || ''} - PRECISA Tecnologia`.replace('  ', ' ').trim()
}
function descricaoPadrao(nf) {
  return [
    `Prezado(a) ${nf?.clienteNome || 'cliente'},`,
    '',
    `Segue em anexo a Nota Fiscal de Serviço Eletrônica (NFS-e) nº ${nf?.numero || ''}` +
      `, no valor de ${formatBRL(nf?.valor)}, referente aos serviços prestados pela PRECISA Tecnologia.`,
    '',
    'Anexos: arquivo XML e PDF (DANFSe) da nota fiscal.',
    '',
    'Atenciosamente,',
    'PRECISA Tecnologia',
  ].join('\n')
}

/** Remove repetidos (por e-mail, sem diferenciar maiúsculas) preservando a ordem. */
function dedup(lista) {
  const vistos = new Set()
  return lista.filter((d) => {
    const chave = (d.email || '').trim().toLowerCase()
    if (!chave || vistos.has(chave)) return false
    vistos.add(chave)
    return true
  })
}

export default function EmailModal({ nf, onClose, onSent }) {
  const toast = useToast()
  const open = !!nf
  const [detalhe, setDetalhe] = useState(null)
  const [destinatarios, setDestinatarios] = useState([])
  const [assunto, setAssunto] = useState('')
  const [descricao, setDescricao] = useState('')
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    if (!nf) return
    // Enquanto o detalhe não chega, o e-mail do cliente (`cliente.email`) serve de partida.
    setDestinatarios(nf.clienteEmail ? [{ nome: nf.clienteNome || '', email: nf.clienteEmail }] : [])
    setAssunto(assuntoPadrao(nf))
    setDescricao(descricaoPadrao(nf))
    setDetalhe(null)
    api.get(`/nfs/${nf.nfID}`).then(({ data }) => {
      setDetalhe(data)
      // Destinatários padrão: os e-mails cadastrados no contrato (`contrato_email`).
      // Sem nenhum cadastrado, cai no e-mail do cliente.
      const doContrato = (data.contratoEmails || []).map((e) => ({ nome: e.nome || '', email: e.email }))
      const iniciais = doContrato.length > 0
        ? doContrato
        : (data.clienteEmail ? [{ nome: data.clienteNome || '', email: data.clienteEmail }] : [])
      setDestinatarios(dedup(iniciais))
    }).catch(() => {})
  }, [nf])

  function atualizar(idx, patch) {
    setDestinatarios((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)))
  }
  function adicionar() {
    setDestinatarios((prev) => [...prev, { nome: '', email: '' }])
  }
  function remover(idx) {
    setDestinatarios((prev) => prev.filter((_, i) => i !== idx))
  }

  const validos = dedup(destinatarios.map((d) => ({ ...d, email: (d.email || '').trim() })))
    .filter((d) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email))

  async function enviar() {
    setEnviando(true)
    try {
      const { data } = await api.post(`/nfs/${nf.nfID}/email`, { destinatarios: validos, assunto, descricao })
      const enviados = data?.enviados?.length ?? validos.length
      const falhas = data?.falhas?.length ?? 0
      if (falhas > 0) {
        toast.error(`${enviados} e-mail(s) enviado(s), ${falhas} com falha: ${data.falhas.map((f) => f.email).join(', ')}`)
      } else {
        toast.success(enviados > 1 ? `${enviados} e-mails enviados com sucesso.` : 'E-mail enviado com sucesso.')
      }
      onSent?.()
      onClose()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Falha ao enviar o e-mail.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Enviar NF por e-mail"
      description={nf ? `NFS-e nº ${nf.numero || ''} · ${nf.clienteNome}` : undefined}
      size="lg"
      footer={<>
        <Button variant="outline" onClick={onClose}>Fechar</Button>
        <Button onClick={enviar} disabled={enviando || validos.length === 0}>
          {enviando ? 'Enviando...' : `Enviar e-mail${validos.length > 1 ? ` (${validos.length})` : ''}`}
        </Button>
      </>}
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label>Destinatários</Label>
            <span className="text-xs text-muted-foreground">Pré-preenchidos com os e-mails do contrato</span>
          </div>
          <div className="space-y-2">
            {destinatarios.map((d, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  value={d.nome || ''}
                  onChange={(e) => atualizar(idx, { nome: e.target.value })}
                  placeholder="Nome (opcional)"
                  className="w-1/3"
                />
                <Input
                  type="email"
                  value={d.email || ''}
                  onChange={(e) => atualizar(idx, { email: e.target.value })}
                  placeholder="cliente@exemplo.com"
                  autoFocus={idx === 0}
                />
                <button
                  onClick={() => remover(idx)}
                  title="Remover destinatário"
                  className="p-2 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            {destinatarios.length === 0 && (
              <p className="text-sm text-muted-foreground">Nenhum destinatário. Adicione ao menos um e-mail.</p>
            )}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={adicionar}>
            <Plus className="h-4 w-4" /> Adicionar destinatário
          </Button>
        </div>
        <div className="space-y-1.5">
          <Label>Assunto</Label>
          <Input value={assunto} onChange={(e) => setAssunto(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Mensagem</Label>
          <textarea
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            rows={7}
            className="flex w-full rounded-md border border-border bg-input px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Paperclip className="h-4 w-4" />
          <span className="inline-flex items-center gap-1"><FileCode2 className="h-4 w-4" /> XML</span>
          <span className="inline-flex items-center gap-1"><FileText className="h-4 w-4" /> PDF (DANFSe)</span>
        </div>

        {detalhe?.emails?.length > 0 && (
          <div>
            <Label className="mb-2 block">Envios anteriores</Label>
            <div className="space-y-1.5 max-h-40 overflow-auto">
              {detalhe.emails.map((em) => (
                <div key={em.nfEmailID} className="flex items-center gap-3 text-sm border-t border-border/60 pt-1.5">
                  <Badge variant={em.status === 'enviado' ? 'success' : 'destructive'}>{em.status}</Badge>
                  <span>{em.destinatario}</span>
                  <span className="text-muted-foreground text-xs ml-auto">{formatDate(em.criadoEm)} · {em.usuarioNome || '—'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
}
